import React from 'react';
import { Box, Grid, Group, Paper, Text, ActionIcon, Tooltip } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { getTargetEntitlementsList } from '../../../types/EntityEntitlements';
import type { EntityEntitlementForTargetResponse } from '../../../types/EntityEntitlements';
import { useViewModeSafe } from '../../../contexts/ViewModeContext';
import { EntityFieldFilterInput } from '../../common/EntityFieldFilterInput';

export interface EntityDetailSummaryProps {
    /** Tier3 target entitlement used to show/hide the field debug strip */
    targetEntitlement?: EntityEntitlementForTargetResponse | null;
    /** Whether tier3 entitlement icons are shown next to field labels */
    showFieldDebugInfo?: boolean;
    /** Toggle tier3 field debug info visibility */
    onToggleFieldDebug?: () => void;
    /** Summary tab content (e.g. result of renderEntityProperties) */
    children: React.ReactNode;
    /** Controlled field filter query (dims non-matching fields in summary). */
    fieldFilterQuery?: string;
    onFieldFilterQueryChange?: (query: string) => void;
    /** Match counts while filtering (shown under filter input). */
    fieldFilterMatchCount?: number;
    fieldFilterTotalCount?: number;
}

/**
 * Summary tab content panel: optional tier3 entitlement debug strip + Grid of entity properties.
 * The actual property rendering is passed as children so the dialog can keep renderEntityProperties
 * or supply it from a future extracted component.
 */
export const EntityDetailSummary: React.FC<EntityDetailSummaryProps> = ({
    targetEntitlement,
    showFieldDebugInfo = false,
    onToggleFieldDebug,
    children,
    fieldFilterQuery = '',
    onFieldFilterQueryChange,
    fieldFilterMatchCount,
    fieldFilterTotalCount,
}) => {
    const { isStandard } = useViewModeSafe();
    return (
    <>
        {onFieldFilterQueryChange != null && (
            <EntityFieldFilterInput
                value={fieldFilterQuery}
                onChange={onFieldFilterQueryChange}
                matchCount={fieldFilterMatchCount}
                totalCount={fieldFilterTotalCount}
            />
        )}
        {!isStandard && targetEntitlement != null && getTargetEntitlementsList(targetEntitlement).length > 0 && (
            <Paper
                variant="outlined"
                p="xs"
                mb="sm"
                bg="var(--mantine-color-default-hover)"
            >
                <Group gap="xs" align="center">
                    <Tooltip
                        label={
                            showFieldDebugInfo
                                ? 'Hide tier3 entitlement info'
                                : 'Show tier3 entitlement info (icons next to each field)'
                        }
                    >
                        <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={onToggleFieldDebug}
                            color={showFieldDebugInfo ? 'primary' : 'default'}
                            aria-label={
                                showFieldDebugInfo ? 'Hide field debug info' : 'Show field debug info'
                            }
                        >
                            <IconInfoCircle size={16} />
                        </ActionIcon>
                    </Tooltip>
                    {showFieldDebugInfo && (
                        <Text size="xs" c="dimmed">
                            Tier3 entitlement icons shown next to field labels
                        </Text>
                    )}
                </Group>
            </Paper>
        )}
        <Grid gutter="lg">{children}</Grid>
    </>
    );
};
